import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, MessageCircle, CalendarDays, Timer, BookOpen, Mail, Scissors,
  Brain, HeartHandshake, GraduationCap, ArrowRight, CheckCircle2, Flame, Bell,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const TEEN_IMG = "https://images.unsplash.com/photo-1758525861622-f4e7ac86a2d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHRlZW5hZ2VyJTIwc3R1ZHlpbmclMjBkZXNrfGVufDB8fHx8MTc4NTM4NzYzM3ww&ixlib=rb-4.1.0&q=85";
const PARENT_IMG = "https://images.unsplash.com/photo-1629360057380-18b15b42e650?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBoZWxwaW5nJTIwdGVlbmFnZXIlMjBob21ld29ya3xlbnwwfHx8fDE3ODUzODc2MzN8MA&ixlib=rb-4.1.0&q=85";

const shadow = "shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]";
const btnPress = "active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-transform duration-150";
const cta = `inline-flex items-center gap-2 bg-[#4D96FF] text-black border-2 border-black rounded-full font-bold ${shadow} ${btnPress}`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" } }),
};

const FEATURES = [
  { icon: MessageCircle, color: "bg-[#4D96FF]", span: "md:col-span-7", title: "Mia — a tutor with a face and a voice",
    text: "An AI big-sister who chats out loud, explains any subject, and knows exactly how to talk to an ADHD brain: short, warm, zero shame. Part teen psychologist, part life coach, part tutor." },
  { icon: CalendarDays, color: "bg-[#FFE066]", span: "md:col-span-5", title: "A calendar that creates urgency",
    text: "Upload the school assessment schedule — Mia reads it and colour-codes every deadline. Red means now. Dopamine-deficient brains need visible urgency, not nagging." },
  { icon: Scissors, color: "bg-[#F2C6DE]", span: "md:col-span-4", title: "Anti-cramming breakdowns",
    text: "One tap splits any assignment into 15–30 minute chunks, front-loaded across the weeks — so the night-before panic never happens." },
  { icon: Timer, color: "bg-[#51CF66]", span: "md:col-span-4", title: "Body-double focus sprints",
    text: "Mia sits with your teen through 10–25 minute sprints, checking in by voice — the ADHD technique that actually gets homework started." },
  { icon: BookOpen, color: "bg-[#FF6B6B]", span: "md:col-span-4", title: "ATAR-aligned study sets",
    text: "Flashcards, quizzes and A-grade notes generated from their own class materials, matched to their state curriculum." },
  { icon: Mail, color: "bg-white", span: "md:col-span-12", title: "A weekly report in your inbox",
    text: "Every Sunday, Mia emails you an honest summary: check-ins, work completed, focus minutes, and the deadlines that need attention next week. You stay in the loop without being the deadline police." },
];

const STEPS = [
  { n: "1", title: "Create your account", text: "Free 7-day trial — $0 today." },
  { n: "2", title: "Tell Mia about your teen", text: "2-minute setup: year level, state, subjects, and their ADHD profile." },
  { n: "3", title: "Mia takes over", text: "Upload the assessment calendar and let Mia coach, remind and hype them to that A." },
];

const FAQS = [
  { q: "How is this different from a normal tutoring app?", a: "Most study apps assume a neurotypical brain. StudySpark is built around ADHD/ADD: visible urgency instead of silent to-do lists, tiny dopamine-rewarded chunks instead of big tasks, body-doubling instead of \"just focus\", and a coach that never shames a missed day." },
  { q: "Which year levels and states does it cover?", a: "Years 7–12 across all Australian states. Mia aligns advice, study notes and marking language to your state's curriculum body — NESA, VCAA, QCAA, SCSA, SACE, TASC or BSSS — plus ATAR standards." },
  { q: "Does my teen need a diagnosis to benefit?", a: "No. During setup you describe your teen's real challenges — starting tasks, time blindness, cramming, motivation — and Mia adapts her coaching to them, diagnosis or not." },
  { q: "What do I see as a parent?", a: "A password-protected parent view with weekly stats, Mia's honest written summary, and upcoming deadlines — plus an automatic email every Sunday. You get visibility without hovering." },
  { q: "Can I cancel anytime?", a: "Yes. Your first 7 days are completely free — if you cancel before the trial ends you pay nothing, otherwise your plan starts automatically. Cancelling later takes two clicks from your billing page. No lock-in." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen text-[#0A0A0A]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#FAFAFA] border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/mascot.png" alt="StudySpark mascot" className="w-10 h-10 rounded-full border-2 border-black bg-white object-cover" />
            <span className="font-heading text-xl font-bold">StudySpark</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-bold">
            <a href="#features" className="hover:text-[#4D96FF] transition-colors" data-testid="nav-features-link">Features</a>
            <a href="#pricing" className="hover:text-[#4D96FF] transition-colors" data-testid="nav-pricing-link">Pricing</a>
            <a href="#faq" className="hover:text-[#4D96FF] transition-colors" data-testid="nav-faq-link">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="nav-login-button" className={`px-4 py-2 text-sm bg-white border-2 border-black rounded-full font-bold ${shadow} ${btnPress}`}>Log in</Link>
            <Link to="/auth" data-testid="nav-trial-button" className={`${cta} px-4 py-2 text-sm hidden sm:inline-flex`}>Start free trial</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-5 pt-14 pb-20 grid md:grid-cols-12 gap-10 items-center">
        <motion.div className="md:col-span-7 space-y-6" initial="hidden" animate="show">
          <motion.p variants={fadeUp} custom={0} className="inline-block text-xs tracking-[0.2em] uppercase font-bold bg-[#FFE066] border-2 border-black rounded-full px-4 py-1.5">
            For parents of ADHD &amp; ADD teens
          </motion.p>
          <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-none tracking-tight">
            The study coach ADHD brains actually <span className="bg-[#F2C6DE] border-2 border-black rounded-2xl px-2 inline-block">listen to.</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-gray-700 leading-relaxed max-w-xl font-medium">
            Meet Mia — an AI tutor, teen psychologist and life coach in one. She tracks every assignment, breaks work into dopamine-sized chunks, and keeps your teen accountable so cramming never happens again.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="flex flex-wrap items-center gap-4">
            <Link to="/auth" data-testid="hero-trial-button" className={`${cta} px-7 py-3.5 text-base`}>
              Start free 7-day trial <ArrowRight size={18} />
            </Link>
            <span className="text-sm font-bold text-gray-600">7 days free · Cancel anytime</span>
          </motion.div>
          <motion.div variants={fadeUp} custom={4} className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold">
            {["Years 7–12", "All AU state curricula", "ATAR-aligned"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#51CF66]" /> {t}</span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div className="md:col-span-5 relative" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <img src="/mascot.png" alt="Sparky the StudySpark mascot" className={`w-full max-w-sm mx-auto rounded-3xl border-2 border-black bg-white ${shadow}`} />
          <div className={`absolute -top-4 -left-2 md:-left-6 bg-[#FF6B6B] text-white border-2 border-black rounded-xl px-3 py-2 text-xs font-bold ${shadow} flex items-center gap-1.5 rotate-[-4deg]`}>
            <Bell size={14} /> Science report due in 2 days!
          </div>
          <div className={`absolute -bottom-4 right-0 md:-right-2 bg-[#FFE066] border-2 border-black rounded-xl px-3 py-2 text-xs font-bold ${shadow} flex items-center gap-1.5 rotate-[3deg]`}>
            <Flame size={14} className="text-[#FF6B6B]" /> 12-day streak
          </div>
        </motion.div>
      </header>

      {/* Empathy strip */}
      <section className="bg-[#0A0A0A] text-white border-y-2 border-black">
        <div className="max-w-6xl mx-auto px-5 py-10 grid sm:grid-cols-3 gap-8">
          {[{ icon: Brain, t: "ADHD psychologist", d: "Coaching techniques built for the dopamine-deficient brain" },
            { icon: HeartHandshake, t: "Life coach", d: "Accountability and hype without shame or nagging" },
            { icon: GraduationCap, t: "A-grade tutor", d: "Every subject, aligned to your state's curriculum" }].map(({ icon: Icon, t, d }, i) => (
            <motion.div key={t} className="flex gap-3 items-start" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <div className="p-2.5 bg-white text-black border-2 border-black rounded-xl shrink-0"><Icon size={20} /></div>
              <div>
                <p className="font-heading font-bold">{t}</p>
                <p className="text-sm text-gray-300 leading-relaxed">{d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-20">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">What's inside</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-10 max-w-2xl">
          Everything a distracted brain needs to get to an A
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {FEATURES.map(({ icon: Icon, color, span, title, text }, i) => (
            <motion.div key={title} className={`${span} ${color} border-2 border-black rounded-2xl p-8 ${shadow}`}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} custom={i % 3}
              data-testid={`feature-card-${i}`}>
              <div className="inline-flex p-3 bg-white border-2 border-black rounded-xl mb-4"><Icon size={22} strokeWidth={2} /></div>
              <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
              <p className="text-sm leading-relaxed font-medium">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#4D96FF] border-y-2 border-black">
        <div className="max-w-6xl mx-auto px-5 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2">How it works</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6">Up and coaching in under 5 minutes</h2>
            <img src={TEEN_IMG} alt="Teenager studying happily at a desk" className={`w-full rounded-2xl border-2 border-black object-cover aspect-[4/3] ${shadow}`} />
          </div>
          <div className="md:col-span-7 space-y-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} className={`flex gap-4 bg-white border-2 border-black rounded-2xl p-6 ${shadow}`}
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <span className="font-heading text-2xl font-bold bg-[#FFE066] border-2 border-black rounded-full w-11 h-11 flex items-center justify-center shrink-0">{s.n}</span>
                <div>
                  <h3 className="font-heading text-xl font-bold">{s.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed font-medium">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-6xl mx-auto px-5 py-20 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <img src={PARENT_IMG} alt="Parent and teenager working together" className={`w-full rounded-2xl border-2 border-black object-cover aspect-[4/3] ${shadow}`} />
        </div>
        <div className="md:col-span-6 space-y-5">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Built by an ADHD parent</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">You stop being the deadline police. Mia takes that job.</h2>
          <p className="text-base text-gray-700 leading-relaxed font-medium">
            StudySpark started as one dad's tutor for his Year 10 daughter with ADD. The fights about homework stopped when the reminders, urgency and hype came from Mia instead of mum and dad. Now every family can have her.
          </p>
          <div className="flex flex-wrap gap-2">
            {["NESA", "VCAA", "QCAA", "SCSA", "SACE", "TASC", "BSSS"].map((c) => (
              <span key={c} className="text-xs font-bold bg-white border-2 border-black rounded-full px-3 py-1.5">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[#FFE066] border-y-2 border-black">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2">Pricing</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">One plan. The whole toolkit.</h2>
          <p className="font-medium mb-10">Every plan starts with a free 7-day trial — $0 today, cancel anytime.</p>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl">
            <motion.div className={`bg-white border-2 border-black rounded-2xl p-8 ${shadow}`} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={0} data-testid="pricing-monthly-card">
              <h3 className="font-heading text-xl font-bold">Monthly</h3>
              <p className="font-heading text-5xl font-bold my-4">A$19<span className="text-lg text-gray-500">/mo</span></p>
              <ul className="space-y-2 text-sm font-medium mb-6">
                {["Unlimited Mia chat + voice", "Assignment calendar & reminders", "Focus sprints & study sets", "Weekly parent reports", "Cancel anytime"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#51CF66] shrink-0" /> {f}</li>
                ))}
              </ul>
              <Link to="/auth" data-testid="pricing-monthly-button" className={`${cta} w-full justify-center px-6 py-3`}>Start free trial</Link>
            </motion.div>
            <motion.div className={`relative bg-[#F2C6DE] border-2 border-black rounded-2xl p-8 ${shadow}`} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1} data-testid="pricing-yearly-card">
              <span className="absolute -top-3.5 right-6 bg-black text-white text-xs font-bold rounded-full px-3 py-1.5">2 MONTHS FREE</span>
              <h3 className="font-heading text-xl font-bold">Yearly</h3>
              <p className="font-heading text-5xl font-bold my-4">A$190<span className="text-lg text-gray-600">/yr</span></p>
              <ul className="space-y-2 text-sm font-medium mb-6">
                {["Everything in Monthly", "Save A$38 a year", "One less thing to remember", "Priority feature access", "Cancel anytime"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#51CF66] shrink-0" /> {f}</li>
                ))}
              </ul>
              <Link to="/auth" data-testid="pricing-yearly-button" className={`${cta} w-full justify-center px-6 py-3 bg-white`}>Start free trial</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 py-20">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">FAQ</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">Questions parents ask</h2>
        <Accordion type="single" collapsible className="space-y-4">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`q${i}`} className={`bg-white border-2 border-black rounded-2xl px-6 ${shadow}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-heading font-bold text-left text-base hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-gray-700 leading-relaxed font-medium">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className={`bg-[#51CF66] border-2 border-black rounded-2xl p-10 md:p-14 ${shadow} grid md:grid-cols-12 gap-8 items-center`}>
          <div className="md:col-span-8 space-y-3">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">This term could be the one that changes everything.</h2>
            <p className="font-medium">Give your teen a coach who gets their brain. Free for 7 days — cancel anytime.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/auth" data-testid="final-cta-button" className={`${cta} bg-white px-7 py-3.5 text-base`}>
              Start free trial <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-black bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/mascot.png" alt="mascot" className="w-8 h-8 rounded-full border-2 border-black bg-white object-cover" />
            <span className="font-heading font-bold flex items-center gap-1.5">StudySpark <Sparkles size={14} className="text-[#FFB800]" /></span>
          </div>
          <p className="text-xs text-gray-500 font-medium">Made with love for ADHD families in Australia · © {new Date().getFullYear()} StudySpark</p>
        </div>
      </footer>
    </div>
  );
}
